import type { StepType } from "@/types/workflow.types";
import type { HighlightLanguage } from "./highlighted-code-editor";

export const FIELD_ORDER: Partial<Record<StepType, string[]>> = {
  transform: ["operation", "expression", "script", "code", "language", "input_mapping", "output_mapping"],
  filter: ["condition", "where", "expression", "operator"],
  validate: ["schema", "rules", "strict", "on_error"],
  lookup: ["connector_id", "databaseName", "schemaName", "table", "key", "foreign_key", "select", "cache_ttl_seconds"],
  aggregate: ["group_by", "metrics", "aggregations", "window"],
  api_extract: ["method", "url", "headers", "query_params", "body", "body_template", "pagination", "limit"],
  api_load: ["method", "url", "headers", "body", "body_template", "batch_size"],
  http_request: ["method", "url", "headers", "query_params", "body", "body_template", "timeout_seconds", "max_retries"],
  notify: [
    "channel",
    "lark",
    "telegram",
    "templateResourceId",
    "inlineTemplate",
    "cardFromRowField",
    "variables",
    "includeJobContext",
    "includeStepContext",
    "includeTimestamp",
  ],
  conditional: ["condition", "branches", "else_step"],
  delay: ["duration_seconds", "until", "timezone"],
  code: ["language", "code", "timeout_seconds"],
  subworkflow: ["workflow_id", "input_mapping", "wait_for_completion"],
  kb_source: ["knowledge_ids", "folder_ids", "tag_ids", "category_ids", "statuses", "publication", "content_mode", "limit", "include_metadata"],
  kb_output: ["mode", "target_knowledge_id", "target_folder_id", "category_id", "tag_ids", "title_template", "content_field", "provenance_mode", "publish"],
  kg_search: ["query", "kind", "limit", "include_orphaned"],
  kg_neighbors: ["entity_id", "depth", "limit", "scope", "include_orphaned"],
  kg_context: ["entity_id", "include_sources", "content_mode"],
};

export const SELECT_OPTIONS: Record<string, Array<{ value: string; label: string }>> = {
  channel: ["lark", "telegram"].map((value) => ({ value, label: value })),
  language: ["javascript", "typescript", "python", "sql"].map((value) => ({ value, label: value })),
  method: ["GET", "POST", "PUT", "PATCH", "DELETE"].map((value) => ({ value, label: value })),
  mode: ["append", "replace", "upsert", "create", "update"].map((value) => ({ value, label: value })),
  on_error: ["fail", "skip", "warn"].map((value) => ({ value, label: value })),
  operator: ["and", "or"].map((value) => ({ value, label: value })),
  content_mode: ["raw", "chunks", "metadata", "ai_context", "summary", "detail", "sources"].map((value) => ({ value, label: value })),
  provenance_mode: ["preserve", "append", "none"].map((value) => ({ value, label: value })),
  publication: ["any", "published", "unpublished"].map((value) => ({ value, label: value })),
  scope: ["enterprise", "personal"].map((value) => ({ value, label: value })),
};

export const CODE_KEYS = new Set([
  "body_template",
  "code",
  "condition",
  "expression",
  "message",
  "script",
  "template",
]);

export const SQL_KEYS = new Set(["query", "sql", "where"]);

const JSON_KEYS = new Set([
  "aggregations",
  "body",
  "branches",
  "headers",
  "metrics",
  "pagination",
  "query_params",
  "rules",
  "schema",
]);

export function isJsonLike(value: unknown) {
  return typeof value === "object" && value !== null;
}

export function stringifyConfigValue(value: unknown) {
  if (typeof value === "string") return value;
  if (value === undefined || value === null) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "";
  }
}

export function humanizeConfigKey(key: string) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function orderedConfigKeys(stepType: StepType, config: Record<string, unknown>) {
  const known = FIELD_ORDER[stepType] ?? [];
  const existing = Object.keys(config);
  return [
    ...known.filter((key) => existing.includes(key)),
    ...existing.filter((key) => !known.includes(key)).sort(),
  ];
}

export function isJsonEditorKey(normalized: string) {
  return JSON_KEYS.has(normalized)
    || normalized.includes("json")
    || normalized.includes("mapping")
    || normalized.endsWith("_schema");
}

export function isCodeEditorKey(normalized: string) {
  return CODE_KEYS.has(normalized);
}

export function isTextEditorKey(normalized: string) {
  return isCodeEditorKey(normalized)
    || normalized.includes("prompt")
    || normalized.includes("template")
    || normalized === "message";
}

export function configEditorLanguage(key: string, value: unknown): HighlightLanguage {
  const normalized = key.toLowerCase();
  if (SQL_KEYS.has(normalized)) return "sql";
  if (isJsonLike(value) || isJsonEditorKey(normalized)) return "json";
  if (normalized === "code" || normalized === "script") return "javascript";
  return "markdown";
}

export function parseStructuredValue(key: string, original: unknown, raw: string): unknown {
  if (isJsonLike(original) || configEditorLanguage(key, original) === "json") {
    return raw.trim() ? JSON.parse(raw) : null;
  }
  return raw;
}
